import { useLocation, Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const ROUTE_MAP = {
  en: {
    "/about": "About",
    "/faq": "FAQ",
    "/aesthetic-guide": "Ask Karine FAQ",
    "/gift-cards": "Gift Cards",
    "/financing": "Financing",
    "/promotions": "Promotions",
    "/before-after": "Before & After",
    "/aftercare": "Aftercare",
    "/my-appointments": "My Account",
    "/rewards": "Rewards",
    "/privacy": "Privacy Policy",
    "/terms": "Terms of Service",
    "/data-deletion": "Data Deletion",
    "/virtual-consultation-guide": "Virtual Consultation",
    "/botox-ottawa": ["Services", "Botox"],
    "/lip-filler-ottawa": ["Services", "Lip Filler"],
    "/microneedling-ottawa": ["Services", "Microneedling"],
    "/dermal-filler-ottawa": ["Services", "Dermal Filler"],
    "/prp-ottawa": ["Services", "PRP"],
    "/sculptra-ottawa": ["Services", "Sculptra"],
    "/sculptra": ["Services", "Sculptra"],
    "/skin-boosters": ["Services", "Skin Boosters"],
    "/admin/hub": ["Admin", "Hub"],
    "/admin/bookings": ["Admin", "Bookings"],
    "/admin/clients": ["Admin", "Clients"],
    "/admin/gift-cards": ["Admin", "Gift Cards"],
    "/admin/referrals": ["Admin", "Referrals"],
    "/admin/subscriptions": ["Admin", "Memberships"],
    "/admin/analytics": ["Admin", "Analytics"],
    "/crown-reserve": "Crown Reserve Rewards",
    "/crown-reserve-plan": "Crown Reserve Aesthetic Plan",
    "/crown-reserve-hub": "Crown Reserve",
  },
  fr: {
    "/about": "À propos",
    "/faq": "FAQ",
    "/aesthetic-guide": "Demandez Karine FAQ",
    "/gift-cards": "Cartes Cadeaux",
    "/financing": "Financement",
    "/promotions": "Promotions",
    "/before-after": "Avant & Après",
    "/aftercare": "Soins post-traitement",
    "/my-appointments": "Mon Compte",
    "/rewards": "Récompenses",
    "/privacy": "Politique de confidentialité",
    "/terms": "Conditions d'utilisation",
    "/data-deletion": "Suppression de données",
    "/virtual-consultation-guide": "Consultation virtuelle",
    "/crown-reserve": "Crown Reserve Récompenses",
    "/crown-reserve-plan": "Plan Esthétique Crown Reserve",
    "/crown-reserve-hub": "Crown Reserve",
    "/botox-ottawa": ["Services", "Botox"],
    "/lip-filler-ottawa": ["Services", "Remplissage lèvres"],
    "/microneedling-ottawa": ["Services", "Microneedling"],
    "/dermal-filler-ottawa": ["Services", "Remplissage dermique"],
    "/prp-ottawa": ["Services", "PRP"],
    "/sculptra-ottawa": ["Services", "Sculptra"],
    "/sculptra": ["Services", "Sculptra"],
    "/skin-boosters": ["Services", "Boosters Cutanés"],
    "/admin/hub": ["Admin", "Hub"],
    "/admin/bookings": ["Admin", "Réservations"],
    "/admin/clients": ["Admin", "Clients"],
    "/admin/gift-cards": ["Admin", "Cartes Cadeaux"],
    "/admin/referrals": ["Admin", "Références"],
    "/admin/subscriptions": ["Admin", "Abonnements"],
    "/admin/analytics": ["Admin", "Analytique"],
  },
};

export default function Breadcrumb() {
  const location = useLocation();
  const { language } = useLanguage();

  if (location.pathname === "/") return null;

  const map = ROUTE_MAP[language] || ROUTE_MAP.en;
  const entry = map[location.pathname];
  if (!entry) return null;

  const isAdmin = location.pathname.startsWith("/admin");
  const homeName = language === "fr" ? "Accueil" : "Home";
  const crumbs = Array.isArray(entry) ? entry : [entry];

  return (
    <div
      data-testid="breadcrumb-nav"
      className={`fixed z-[39] left-0 right-0 pointer-events-none ${
        isAdmin ? "top-2 left-4" : "top-[58px] md:top-[115px]"
      }`}
    >
      <div className={`${isAdmin ? "max-w-7xl mx-auto px-6 py-1" : "max-w-7xl mx-auto px-3 md:px-6 py-1"}`}>
        <nav
          aria-label="Breadcrumb"
          className={`pointer-events-auto inline-flex items-center gap-1 text-[10px] md:text-xs px-2.5 md:px-3 py-0.5 md:py-1 rounded-full backdrop-blur-md ${
            isAdmin
              ? "bg-zinc-800/80 text-zinc-400"
              : "bg-white/80 text-stone-500 border border-stone-200/60 shadow-sm"
          }`}
        >
          <Link
            to="/"
            className={`flex items-center gap-0.5 md:gap-1 transition-colors ${
              isAdmin ? "text-amber-400 hover:text-amber-300" : "text-amber-700 hover:text-amber-900"
            }`}
            data-testid="breadcrumb-home"
          >
            <Home className="w-2.5 h-2.5 md:w-3 md:h-3" />
            <span>{homeName}</span>
          </Link>
          {crumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-0.5 md:gap-1">
              <ChevronRight className="w-2.5 h-2.5 md:w-3 md:h-3 opacity-40" />
              <span className={`${
                i === crumbs.length - 1
                  ? isAdmin ? "text-zinc-200 font-medium" : "text-stone-800 font-medium"
                  : ""
              }`}>
                {crumb}
              </span>
            </span>
          ))}
        </nav>
      </div>
    </div>
  );
}
